import React from 'react';
import { Layout, Avatar, Dropdown, Space, Typography, Button, Badge, notification } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  BellOutlined,
  GlobalOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { TenantContext, UserProfile } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { useTranslation } from '../../hooks/useTranslation';

const { Header } = Layout;
const { Text } = Typography;

interface TenantHeaderProps {
  tenant: TenantContext;
  user: UserProfile;
  isMobile?: boolean;
  onMenuClick?: () => void;
}

export const TenantHeader: React.FC<TenantHeaderProps> = ({ tenant, user, isMobile, onMenuClick }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useTranslation();

  const handleLogout = () => {
    logout();
    navigate('/login');
    notification.success({
      message: 'Logged out successfully',
      description: 'You have been logged out of the system.',
    });
  };

  // No organization switching needed - removed

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'My Profile',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Account Settings',
      onClick: () => navigate('/settings'),
    },
    // Removed organization switching - users belong to single org from JWT
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      onClick: handleLogout,
    },
  ];

  // No organization switching needed for single-org users
  // Platform users can still access organization management
  const organizationMenuItems: MenuProps['items'] = user.user_type === 'platform_user' ? [
    {
      key: 'all-orgs', 
      label: 'All Organizations',
      onClick: () => navigate('/admin/organizations'),
    },
  ] : [];

  return (
    <Header
      style={{
        background: '#fff',
        padding: isMobile ? '0 12px' : '0 24px',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...(isMobile ? { position: 'sticky', top: 0, zIndex: 10 } : {}),
      }}
    >
      <div>
        {isMobile && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={onMenuClick}
            aria-label={t('common.openMenu')}
            style={{ width: 44, height: 44 }}
          />
        )}
      </div>

      <Space size="middle">
        {/* Organization Switcher for Platform Users */}
        {user.user_type === 'platform_user' && organizationMenuItems.length > 0 && (
          <Dropdown
            menu={{ items: organizationMenuItems }}
            placement="bottomRight"
            arrow
          >
            <Button type="text" icon={<GlobalOutlined />}>
              {tenant.organization?.name}
            </Button>
          </Dropdown>
        )}

        {/* Language Switcher */}
        <LanguageSwitcher compact={true} />

        {/* Notifications */}
        <Dropdown
          menu={{
            items: [
              {
                key: 'no-notifications',
                label: 'No new notifications',
                disabled: true,
              },
            ]
          }}
          placement="bottomRight"
          arrow
        >
          <Badge count={0}>
            <Button
              type="text"
              icon={<BellOutlined />}
              style={{ display: 'flex', alignItems: 'center' }}
            />
          </Badge>
        </Dropdown>

        {/* User Menu */}
        <Dropdown
          menu={{ items: userMenuItems }}
          placement="bottomRight"
          arrow
        >
          <Space style={{ cursor: 'pointer' }}>
            <Avatar
              size="small"
              style={{
                backgroundColor: tenant.branding?.secondary_color || '#1890ff',
              }}
            >
              {user.name?.charAt(0).toUpperCase()}
            </Avatar>
            {!isMobile && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text strong style={{ fontSize: 14, lineHeight: 1.2 }}>
                  {user.name}
                </Text>
                <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.2 }}>
                  {user.user_type === 'platform_user' ? 'Platform Admin' : 'Organization User'}
                </Text>
              </div>
            )}
          </Space>
        </Dropdown>
      </Space>
    </Header>
  );
};